import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Building, ChevronDown, ArrowRight, CheckCircle } from 'lucide-react';
import { FirebaseAuthService } from '../services/firebaseAuthService';
import { UsuarioInativoAlert } from '../components/auth/UsuarioInativoAlert';
import { UsuarioInativoModal } from '../components/auth/UsuarioInativoModal';
import { inactiveUserService } from '../services/inactiveUserService';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [unit, setUnit] = useState('matriz');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Depois de logar, volta para a página que o usuário tentou acessar antes
  // de ser mandado para o login (ex: link de OS recebido por WhatsApp) —
  // só aceita caminhos internos (começando com "/") para evitar redirecionar
  // para um domínio externo.
  const getRedirectDestino = () => {
    const redirect = new URLSearchParams(location.search).get('redirect');
    return redirect && redirect.startsWith('/') ? redirect : '/';
  };

  // Estados para alerta e modal de Usuário Inativo
  const [isInactiveUser, setIsInactiveUser] = useState(false);
  const [showInactiveModal, setShowInactiveModal] = useState(false);
  const [inactiveDetails, setInactiveDetails] = useState<{ email?: string; name?: string }>({});

  useEffect(() => {
    // Verifica se veio aviso prévio salvo pelo AuthContext ou URL
    const searchParams = new URLSearchParams(location.search);
    const isInactiveUrl = searchParams.get('inativo') === 'true' || searchParams.get('inactive') === 'true';
    const emailParam = searchParams.get('email') || '';

    const pendingNotice = inactiveUserService.getInactiveNotice();

    if (isInactiveUrl || pendingNotice) {
      const emailToUse = emailParam || pendingNotice?.email || '';
      const nameToUse = pendingNotice?.name || '';
      setIsInactiveUser(true);
      setShowInactiveModal(true);
      setInactiveDetails({ email: emailToUse, name: nameToUse });
      if (emailToUse && !email) {
        setEmail(emailToUse);
      }
      inactiveUserService.clearInactiveNotice();
    }
  }, [location]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsInactiveUser(false);

    if (!email.trim()) {
      setError('Por favor, informe o e-mail.');
      return;
    }
    if (!password.trim()) {
      setError('Por favor, informe a senha com pelo menos 6 caracteres.');
      return;
    }

    setIsLoading(true);

    try {
      // Autenticação direta no Firebase Auth
      const { user } = await FirebaseAuthService.loginWithEmailPassword(email, password);

      if (user) {
        // Verifica se o usuário foi excluído do sistema
        const isDeleted = await FirebaseAuthService.isUserDeleted(user.uid, email.trim());
        if (isDeleted) {
          await FirebaseAuthService.logout();
          setIsInactiveUser(true);
          setShowInactiveModal(true);
          setInactiveDetails({
            email: email.trim(),
            name: user.displayName || email.split('@')[0]
          });
          inactiveUserService.setInactiveNotice(
            email.trim(),
            user.displayName || '',
            'Sua conta foi excluída do sistema pelo administrador. O acesso foi cancelado.'
          );
          setIsLoading(false);
          return;
        }

        // Verifica se o usuário possui perfil inativo no Firestore
        const profile = await FirebaseAuthService.getUserProfile(user.uid, email.trim());
        if (profile && profile.active === false) {
          await FirebaseAuthService.logout();
          setIsInactiveUser(true);
          setShowInactiveModal(true);
          setInactiveDetails({
            email: email.trim(),
            name: profile.displayName || ''
          });
          inactiveUserService.setInactiveNotice(email.trim(), profile.displayName || '');
          setIsLoading(false);
          return;
        }

        // Limpa avisos residuais caso o login seja bem-sucedido
        inactiveUserService.clearInactiveNotice();
        localStorage.setItem('userUnit', unit);
        navigate(getRedirectDestino());
      }
    } catch (err: any) {
      const code = err?.code || '';
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/user-not-found' ||
        code === 'auth/wrong-password'
      ) {
        setError('E-mail ou senha incorretos no Firebase Auth.');
      } else if (code === 'auth/too-many-requests') {
        setError('Muitas tentativas sem sucesso. Aguarde alguns instantes e tente novamente.');
      } else if (code === 'auth/invalid-email') {
        setError('O formato do e-mail informado é inválido.');
      } else if (code === 'auth/user-disabled') {
        setIsInactiveUser(true);
        setShowInactiveModal(true);
        setInactiveDetails({ email: email.trim(), name: '' });
      } else {
        setError(err.message || 'Erro ao autenticar no Firebase.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickResetPassword = async () => {
    if (!email.trim()) {
      setError('Por favor, digite o e-mail no campo acima para enviar o link de redefinição.');
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await FirebaseAuthService.sendPasswordReset(email);
      setSuccess(`Link de redefinição de senha enviado para ${email.trim()}! Verifique a caixa de entrada.`);
    } catch (err: any) {
      if (err?.code === 'auth/user-not-found') {
        setError('Este e-mail ainda não possui conta criada no Firebase Auth.');
      } else {
        setError(err.message || 'Erro ao enviar link de recuperação de senha.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setIsGoogleLoading(true);
    try {
      const { user } = await FirebaseAuthService.loginWithGoogle();
      if (user) {
        const userEmail = (user.email || '').trim().toLowerCase();
        const isDeleted = await FirebaseAuthService.isUserDeleted(user.uid, userEmail);
        if (isDeleted) {
          await FirebaseAuthService.logout();
          setIsInactiveUser(true);
          setShowInactiveModal(true);
          setInactiveDetails({
            email: userEmail,
            name: user.displayName || userEmail.split('@')[0]
          });
          inactiveUserService.setInactiveNotice(
            userEmail,
            user.displayName || '',
            'Sua conta foi excluída do sistema pelo administrador. O acesso foi revogado.'
          );
          return;
        }

        localStorage.setItem('userUnit', unit);
        navigate(getRedirectDestino());
      }
    } catch (err: any) {
      if (err?.code !== 'auth/popup-closed-by-user') {
        setError(err?.message || 'Erro no login com Google.');
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-gray-900 font-sans">
      {/* Vídeo de Fundo (Caminhões/Rodovia) com Overlay Escuro */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source
            src="https://videos.pexels.com/video-files/3121459/3121459-hd_1920_1080_24fps.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/80 backdrop-blur-[2px]"></div>
      </div>

      {/* Card de Login */}
      <div className="relative z-10 flex flex-col md:flex-row w-full max-w-5xl bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden m-4 min-h-[550px] border border-white/20">
        
        {/* Lado Esquerdo (Vermelho/Branding) */}
        <div className="hidden md:flex flex-col bg-gradient-to-br from-red-600 via-red-700 to-red-900 w-1/2 p-12 justify-between text-white relative overflow-hidden">
          {/* Elementos decorativos de fundo */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-full h-1/2 bg-gradient-to-t from-black/50 to-transparent"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center mb-12">
              <span className="text-white text-6xl font-black mr-2 tracking-tighter drop-shadow-lg">V</span>
              <div className="flex flex-col text-left">
                <span className="text-white text-3xl font-black leading-none tracking-tight drop-shadow-md">Voope TWO</span>
                <span className="text-red-200 text-[0.65rem] font-bold tracking-[0.25em]">SISTEMA DE GESTÃO</span>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl font-bold leading-tight">
                Acelerando o seu <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-red-200">negócio.</span>
              </h2>
              <p className="text-red-100 text-lg font-light max-w-sm leading-relaxed">
                Gestão inteligente de frotas e logística integrada com Firebase Auth.
              </p>
            </div>
          </div>

          {/* Card Esporte */}
          <div className="relative z-10 mt-12 bg-white/10 backdrop-blur-md p-6 rounded-xl border border-white/20 shadow-xl transform hover:-translate-y-1 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <p className="text-white text-sm font-semibold tracking-wide uppercase">Apoiando o Esporte</p>
              <span className="px-2 py-1 bg-red-500/50 rounded text-xs font-bold">JIU-JITSU</span>
            </div>
            <div className="h-3 bg-gray-900 w-full rounded-full overflow-hidden shadow-inner relative">
               <div className="absolute left-6 h-full w-16 bg-red-600"></div>
            </div>
          </div>
        </div>

        {/* Lado Direito (Formulário) */}
        <div className="flex flex-col w-full md:w-1/2 p-8 md:p-14 justify-center bg-white">
          
          {/* Logo Mobile */}
          <div className="flex md:hidden items-center justify-center mb-8">
            <span className="text-red-600 text-5xl font-black mr-1 tracking-tighter">V</span>
            <div className="flex flex-col text-left">
              <span className="text-gray-900 text-2xl font-black leading-none tracking-tight">Voope TWO</span>
              <span className="text-gray-500 text-[0.5rem] font-bold tracking-[0.2em]">SISTEMA DE GESTÃO</span>
            </div>
          </div>

          <div className="mb-6 text-center md:text-left">
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              Acesse sua conta
            </h3>
            <p className="text-gray-500 text-sm">
              Insira suas credenciais do Firebase Auth para entrar.
            </p>
          </div>

          <form onSubmit={handleAuth} className="flex flex-col gap-4">
            
            {/* Alerta de Usuário Inativo com Contato com a Transportadora */}
            {isInactiveUser && (
              <div className="mb-2">
                <UsuarioInativoAlert 
                  userEmail={inactiveDetails.email || email}
                  userName={inactiveDetails.name}
                  onDismiss={() => setIsInactiveUser(false)}
                  variant="card"
                />
              </div>
            )}

            {error && !isInactiveUser && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-3.5 text-sm rounded-r-md flex flex-col gap-1.5">
                <span className="font-semibold">{error}</span>
                {email && (
                  <div className="text-xs text-red-600 flex items-center gap-1 mt-0.5">
                    <span>Deseja redefinir ou cadastrar sua senha?</span>
                    <button
                      type="button"
                      onClick={handleQuickResetPassword}
                      className="font-bold underline text-red-700 hover:text-red-900 cursor-pointer ml-1"
                    >
                      Enviar link de senha para este e-mail
                    </button>
                  </div>
                )}
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 border-l-4 border-emerald-500 text-emerald-700 p-3.5 text-sm rounded-r-md flex items-center gap-2">
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {/* Campo Usuário/E-mail */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">E-mail</label>
              <div className="relative group">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-red-500 transition-colors" />
                <input
                  type="email"
                  required
                  placeholder="usuario@voopetwo.com.br"
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-gray-900 placeholder-gray-400 font-medium"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Campo Senha */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Senha</label>
                <Link to="/forgot-password" className="text-xs font-semibold text-red-600 hover:text-red-500 transition-colors">
                  Esqueceu a senha?
                </Link>
              </div>
              <div className="relative group">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-red-500 transition-colors" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength={6}
                  placeholder="••••••••"
                  className="w-full pl-12 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all text-gray-900 placeholder-gray-400 font-medium tracking-wide"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Campo Unidade/Filial */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Unidade</label>
              <div className="relative group">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5 group-focus-within:text-red-500 transition-colors" />
                <select
                  className="w-full pl-12 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 appearance-none text-gray-900 font-medium transition-all cursor-pointer"
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                >
                  <option value="matriz">Matriz - São Paulo</option>
                  <option value="filial_rj">Filial - Rio de Janeiro</option>
                  <option value="filial_mg">Filial - Minas Gerais</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5 pointer-events-none" />
              </div>
            </div>

            {/* Botão Entrar com E-mail */}
            <button
              type="submit"
              disabled={isLoading || isGoogleLoading}
              className="mt-4 w-full flex items-center justify-center gap-2 py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold text-base shadow-[0_8px_20px_rgba(220,38,38,0.3)] hover:shadow-[0_8px_25px_rgba(220,38,38,0.4)] transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  Entrar no Sistema
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>

            {/* Divisor */}
            <div className="relative flex items-center justify-center my-1">
              <div className="border-t border-gray-200 w-full"></div>
              <span className="bg-white px-3 text-xs text-gray-400 font-semibold uppercase">ou</span>
              <div className="border-t border-gray-200 w-full"></div>
            </div>

            {/* Botão Google Auth */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading || isGoogleLoading}
              className="w-full flex items-center justify-center gap-3 py-3 border border-gray-200 rounded-xl font-semibold text-gray-700 bg-white hover:bg-gray-50 shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              {isGoogleLoading ? (
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.17 0 9.97 0 12s.45 3.83 1.25 5.42l4.03-3.15z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                    />
                  </svg>
                  <span>Continuar com Google</span>
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              &copy; {new Date().getFullYear()} Voope TWO. Autenticado com Firebase Auth.
            </p>
          </div>
        </div>
      </div>

      {/* Modal de Detalhes de Usuário Inativo */}
      <UsuarioInativoModal
        isOpen={showInactiveModal}
        onClose={() => setShowInactiveModal(false)}
        userEmail={inactiveDetails.email || email}
        userName={inactiveDetails.name}
        motivo={inactiveUserService.getInactiveNotice()?.message}
      />
    </div>
  );
}
